import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Table, Modal, Input, DatePicker, message, Popconfirm, Typography, Tabs } from 'antd';
import { PlusOutlined, CopyOutlined, DeleteOutlined, BookOutlined, KeyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Clipboard from 'clipboard';
import styles from './index.module.scss';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { getApiKeys, createApiKey, deleteApiKey } from '../../services/index';
import ApiKeyGuide from './ApiKeyGuide';

const { Title, Text } = Typography;

const ApiKeys = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const userId = useSelector((state) => state.global.userId);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiresAt, setNewKeyExpiresAt] = useState(null);
  const [createdKey, setCreatedKey] = useState('');

  const fetchApiKeys = useCallback(async () => {
    setLoading(true);
    try {
      const sessionId = localStorage.getItem('session_id');
      const response = await getApiKeys({ session_id: sessionId });
      if (response.data && response.data.api_keys) {
        setApiKeys(
          response.data.api_keys.map((item, index) => ({
            ...item,
            _rowId: item.id ?? item.key_id ?? item.name ?? index,
            _maskedKey: item.key_prefix ? `${item.key_prefix}***` : '***',
          }))
        );
      }
    } catch (error) {
      message.error(t('apiKeys.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const sessionId = localStorage.getItem('session_id') || '';
    if (!sessionId) {
      dispatch({
        type: 'global/setShowLogin',
        payload: true,
      });
      return;
    }

    fetchApiKeys();
  }, [dispatch, fetchApiKeys, userId]);

  useEffect(() => {
    const clipboard = new Clipboard('.copy-btn');
    clipboard.on('success', () => {
      message.success(t('apiKeys.keyCopied'));
    });
    return () => {
      clipboard.destroy();
    };
  }, [createdKey, t]);

  const closeCreateModal = () => {
    setModalVisible(false);
    setNewKeyName('');
    setNewKeyExpiresAt(null);
    setCreatedKey('');
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      message.error(t('apiKeys.enterKeyName'));
      return;
    }

    setLoading(true);
    try {
      const sessionId = localStorage.getItem('session_id');
      const response = await createApiKey({
        session_id: sessionId,
        name: newKeyName,
        expires_at: newKeyExpiresAt ? newKeyExpiresAt.toISOString() : undefined,
      });
      
      const newApiKey = response?.data?.api_key || '';
      if (response.data && newApiKey) {
        setCreatedKey(newApiKey);
        message.success(t('apiKeys.createSuccess'));
        setNewKeyName('');
        setNewKeyExpiresAt(null);
        fetchApiKeys();
      }
    } catch (error) {
      message.error(t('apiKeys.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    setLoading(true);
    try {
      const sessionId = localStorage.getItem('session_id');
      await deleteApiKey({
        session_id: sessionId,
        key_id: keyId,
      });
      message.success(t('apiKeys.deleteSuccess'));
      fetchApiKeys();
    } catch (error) {
      message.error(t('apiKeys.deleteFailed'));
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: t('apiKeys.name'),
      dataIndex: 'name',
      key: 'name',
      width: '20%',
    },
    {
      title: t('apiKeys.apiKey'),
      dataIndex: '_maskedKey',
      key: 'key',
      width: '40%',
      render: (text) => <Text code style={{ fontFamily: 'monospace' }}>{text || '***'}</Text>,
    },
    {
      title: t('apiKeys.created'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: '20%',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: t('apiKeys.expires'),
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: '20%',
      render: (text) => {
        if (!text) {
          return t('apiKeys.never');
        }
        const expiresAt = dayjs(text);
        return expiresAt.isValid() ? expiresAt.format('YYYY-MM-DD HH:mm:ss') : t('apiKeys.never');
      },
    },
    {
      title: t('apiKeys.action'),
      key: 'action',
      width: '20%',
      render: (_, record) => (
        <Popconfirm
          title={t('apiKeys.deleteConfirm')}
          onConfirm={() => handleDeleteKey(record.id ?? record.key_id)}
          okText={t('common.confirm')}
          cancelText={t('common.cancel')}
        >
          <Button type="text" danger icon={<DeleteOutlined />}>
            {t('apiKeys.delete')}
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className={styles.apiKeysPage}>
      <Header />
      
      <main className={styles.container}>
        <div className={styles.content}>
          <div className={styles.header}>
            <div>
              <Title level={2}>{t('apiKeys.pageTitle')}</Title>
              <Text type="secondary">
                {t('apiKeys.pageSubtitle')}
              </Text>
            </div>
          </div>

          <Tabs
            defaultActiveKey="management"
            size="large"
            className={styles.pageTabs}
            items={[
              {
                key: 'management',
                label: (
                  <span><KeyOutlined /> {t('apiKeys.tabManagement')}</span>
                ),
                children: (
                  <>
                    <div style={{ marginBottom: 16, textAlign: 'right' }}>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setModalVisible(true)}
                        size="large"
                      >
                        {t('apiKeys.createNew')}
                      </Button>
                    </div>
                    <div className={styles.tableWrapper}>
                      <Table
                        columns={columns}
                        dataSource={apiKeys}
                        loading={loading}
                        rowKey={(record) => record._rowId}
                        pagination={{
                          pageSize: 10,
                          showSizeChanger: true,
                          showTotal: (total) => t('apiKeys.totalKeys', { total }),
                        }}
                      />
                    </div>
                  </>
                ),
              },
              {
                key: 'guide',
                label: (
                  <span><BookOutlined /> {t('apiKeys.tabGuide')}</span>
                ),
                children: <ApiKeyGuide />,
              },
            ]}
          />
        </div>
      </main>

      <Footer />

      <Modal
        title={t('apiKeys.createNew')}
        open={modalVisible}
        onOk={createdKey ? closeCreateModal : handleCreateKey}
        onCancel={closeCreateModal}
        okText={createdKey ? t('common.close') : t('apiKeys.create')}
        cancelButtonProps={{ style: createdKey ? { display: 'none' } : {} }}
        confirmLoading={loading}
      >
        {!createdKey ? (
          <div>
            <Text>{t('apiKeys.enterKeyNameLabel')}</Text>
            <Input
              placeholder={t('apiKeys.enterKeyNamePlaceholder')}
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              style={{ marginTop: 10 }}
              onPressEnter={handleCreateKey}
            />
            <Text style={{ display: 'block', marginTop: 12 }}>{t('apiKeys.expireTimeOptional')}</Text>
            <DatePicker
              showTime
              value={newKeyExpiresAt}
              onChange={setNewKeyExpiresAt}
              style={{ width: '100%', marginTop: 8 }}
              placeholder={t('apiKeys.selectExpireTime')}
            />
          </div>
        ) : (
          <div>
            <Text type="success" strong>{t('apiKeys.createSuccess')}</Text>
            <div style={{ marginTop: 16, padding: 16, background: '#f5f5f5', borderRadius: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('apiKeys.saveNowTip')}
              </Text>
              <Input.TextArea
                value={createdKey}
                readOnly
                rows={3}
                style={{ marginTop: 8, fontFamily: 'monospace' }}
              />
              <Button
                type="primary"
                icon={<CopyOutlined />}
                className="copy-btn"
                data-clipboard-text={createdKey}
                style={{ marginTop: 8 }}
                block
              >
                {t('apiKeys.copyToClipboard')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ApiKeys;
